/**
 * Calendar-day <-> UTC-instant conversion for a named IANA time zone.
 *
 * A few API fields are business DAYS that the backend stores as an
 * instant: local midnight of that day in the ACCOUNT's time zone,
 * serialized as UTC. `orders.expectedDeliveryDate` is the first one.
 * Reading such a field through the browser's own zone shifts the day
 * for anyone sitting outside the account's zone, and writing one from
 * the browser's midnight stores the neighbouring day — so both
 * directions name the zone explicitly.
 *
 * Built on `Intl.DateTimeFormat` rather than `moment-timezone`: the
 * zone database already ships with the platform, and pulling the
 * moment bundle in for two conversions would add the whole dataset to
 * the build.
 */

export const DEFAULT_ACCOUNT_TIME_ZONE = 'America/New_York';

const DAY_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * Offset of `timeZone` from UTC, in milliseconds, at `timestamp`.
 * Positive east of Greenwich. Derived by reading the zone's wall clock
 * at that instant and subtracting the instant itself.
 */
const getZoneOffsetMs = (timestamp, timeZone) => {
  const whole = Math.floor(timestamp / 1000) * 1000;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(whole));

  const read = type => Number(parts.find(part => part.type === type)?.value);
  // Some engines render midnight as hour 24 under `hour12: false`.
  const wallClock = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second')
  );

  return wallClock - whole;
};

/**
 * UTC instant -> 'MM/DD/YYYY' as the day reads in `timeZone`.
 * Returns '' for an empty or unparsable value so form fields stay
 * blank rather than showing the table's '-' placeholder.
 */
export const toZonedDay = (value, timeZone = DEFAULT_ACCOUNT_TIME_ZONE) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timeZone || DEFAULT_ACCOUNT_TIME_ZONE,
  });
};

/**
 * 'MM/DD/YYYY' -> ISO string for local midnight of that day in
 * `timeZone`. Returns null when the input is not a full date.
 */
export const fromZonedDay = (day, timeZone = DEFAULT_ACCOUNT_TIME_ZONE) => {
  const match = DAY_PATTERN.exec(String(day ?? '').trim());
  if (!match) return null;

  const zone = timeZone || DEFAULT_ACCOUNT_TIME_ZONE;
  const [, month, dayOfMonth, year] = match;
  const wallClock = Date.UTC(Number(year), Number(month) - 1, Number(dayOfMonth));

  // Two passes: the first offset is read at an instant that can sit on
  // the wrong side of a DST transition, and re-reading it at the
  // corrected instant settles the boundary days.
  const firstPass = wallClock - getZoneOffsetMs(wallClock, zone);
  const instant = wallClock - getZoneOffsetMs(firstPass, zone);

  return new Date(instant).toISOString();
};
